import React, { useState, useEffect, useRef } from "react";
import * as THREE from "three";

// ==================== ENHANCED INTERACTIVE 3D BACKGROUND ====================
const ThreeBackground = () => {
  const mountRef = useRef(null);
  const mouseX = useRef(0);
  const mouseY = useRef(0);

  useEffect(() => {
    if (!mountRef.current) return;

    const currentMount = mountRef.current; // Store ref value

    const scene = new THREE.Scene();
    scene.background = null;
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000,
    );
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });

    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);

    renderer.domElement.style.position = "fixed";
    renderer.domElement.style.top = "0";
    renderer.domElement.style.left = "0";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    renderer.domElement.style.zIndex = "0";

    currentMount.appendChild(renderer.domElement);

    // Particles with interactive glow - INCREASED COUNT
    const particlesCount = window.innerWidth < 768 ? 200 : 350;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particlesCount * 3);
    const velocities = new Float32Array(particlesCount * 3);

    for (let i = 0; i < particlesCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 35;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 35;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 35;

      velocities[i * 3] = (Math.random() - 0.5) * 0.015;
      velocities[i * 3 + 1] = (Math.random() - 0.5) * 0.015;
      velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.015;
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      size: 0.1,
      color: 0xf59e0b,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
    });

    const particles = new THREE.Points(geometry, material);
    scene.add(particles);

    // Add interactive floating geometric shapes
    const shapes = [];

    // Torus
    for (let i = 0; i < 3; i++) {
      const torusGeometry = new THREE.TorusGeometry(0.5, 0.15, 16, 100);
      const torusMaterial = new THREE.MeshBasicMaterial({
        color: 0xf59e0b,
        transparent: true,
        opacity: 0.15,
        wireframe: true,
      });
      const torus = new THREE.Mesh(torusGeometry, torusMaterial);
      torus.position.set(
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 10,
      );
      torus.userData.velocity = {
        x: (Math.random() - 0.5) * 0.008,
        y: (Math.random() - 0.5) * 0.008,
        rotX: (Math.random() - 0.5) * 0.01,
        rotY: (Math.random() - 0.5) * 0.01,
      };
      shapes.push(torus);
      scene.add(torus);
    }

    // Octahedrons
    for (let i = 0; i < 4; i++) {
      const octaGeometry = new THREE.OctahedronGeometry(0.4);
      const octaMaterial = new THREE.MeshBasicMaterial({
        color: 0xfbbf24,
        transparent: true,
        opacity: 0.2,
        wireframe: true,
      });
      const octa = new THREE.Mesh(octaGeometry, octaMaterial);
      octa.position.set(
        (Math.random() - 0.5) * 25,
        (Math.random() - 0.5) * 25,
        (Math.random() - 0.5) * 12,
      );
      octa.userData.velocity = {
        x: (Math.random() - 0.5) * 0.01,
        y: (Math.random() - 0.5) * 0.01,
        rotX: (Math.random() - 0.5) * 0.015,
        rotY: (Math.random() - 0.5) * 0.015,
        rotZ: (Math.random() - 0.5) * 0.015,
      };
      shapes.push(octa);
      scene.add(octa);
    }

    // Icosahedrons
    for (let i = 0; i < 3; i++) {
      const icoGeometry = new THREE.IcosahedronGeometry(0.35);
      const icoMaterial = new THREE.MeshBasicMaterial({
        color: 0xfcd34d,
        transparent: true,
        opacity: 0.18,
        wireframe: true,
      });
      const ico = new THREE.Mesh(icoGeometry, icoMaterial);
      ico.position.set(
        (Math.random() - 0.5) * 22,
        (Math.random() - 0.5) * 22,
        (Math.random() - 0.5) * 11,
      );
      ico.userData.velocity = {
        x: (Math.random() - 0.5) * 0.009,
        y: (Math.random() - 0.5) * 0.009,
        rotX: (Math.random() - 0.5) * 0.012,
        rotY: (Math.random() - 0.5) * 0.012,
        rotZ: (Math.random() - 0.5) * 0.012,
      };
      shapes.push(ico);
      scene.add(ico);
    }

    camera.position.z = 10;

    const handleMouseMove = (e) => {
      mouseX.current = (e.clientX / window.innerWidth) * 2 - 1;
      mouseY.current = -(e.clientY / window.innerHeight) * 2 + 1;
    };

    const handleClick = () => {
      // Pulse effect on click
      shapes.forEach((shape) => {
        shape.material.opacity = 0.4;
        setTimeout(() => {
          shape.material.opacity = shape.geometry.type.includes("Torus")
            ? 0.15
            : 0.2;
        }, 200);
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("click", handleClick);

    let frameId;
    const animate = () => {
      frameId = requestAnimationFrame(animate);

      const positions = particles.geometry.attributes.position.array;

      for (let i = 0; i < particlesCount; i++) {
        positions[i * 3] += velocities[i * 3];
        positions[i * 3 + 1] += velocities[i * 3 + 1];
        positions[i * 3 + 2] += velocities[i * 3 + 2];

        // Boundary check with mouse interaction
        if (Math.abs(positions[i * 3]) > 15) velocities[i * 3] *= -1;
        if (Math.abs(positions[i * 3 + 1]) > 15) velocities[i * 3 + 1] *= -1;
        if (Math.abs(positions[i * 3 + 2]) > 15) velocities[i * 3 + 2] *= -1;

        // Mouse repulsion effect
        const dx = positions[i * 3] - mouseX.current * 5;
        const dy = positions[i * 3 + 1] - mouseY.current * 5;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 3) {
          velocities[i * 3] += dx * 0.001;
          velocities[i * 3 + 1] += dy * 0.001;
        }
      }

      particles.geometry.attributes.position.needsUpdate = true;

      // Smooth camera follow mouse
      camera.position.x += (mouseX.current * 2 - camera.position.x) * 0.02;
      camera.position.y += (mouseY.current * 2 - camera.position.y) * 0.02;
      camera.lookAt(scene.position);

      particles.rotation.x += 0.0003;
      particles.rotation.y += 0.0005;

      // Animate geometric shapes with mouse interaction
      shapes.forEach((shape, i) => {
        shape.position.x += shape.userData.velocity.x;
        shape.position.y += shape.userData.velocity.y;

        if (Math.abs(shape.position.x) > 12) shape.userData.velocity.x *= -1;
        if (Math.abs(shape.position.y) > 12) shape.userData.velocity.y *= -1;

        shape.rotation.x += shape.userData.rotX;
        shape.rotation.y += shape.userData.rotY;
        if (shape.userData.rotZ) shape.rotation.z += shape.userData.rotZ;

        // Mouse attraction/repulsion
        const shapeMouseDist = Math.sqrt(
          Math.pow(shape.position.x - mouseX.current * 5, 2) +
            Math.pow(shape.position.y - mouseY.current * 5, 2),
        );

        if (shapeMouseDist < 4) {
          shape.material.opacity = Math.min(shape.material.opacity + 0.01, 0.5);
          shape.rotation.x += 0.01;
          shape.rotation.y += 0.01;
        } else {
          const baseOpacity = shape.geometry.type.includes("Torus")
            ? 0.15
            : 0.2;
          shape.material.opacity +=
            (baseOpacity - shape.material.opacity) * 0.05;
        }
      });

      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("click", handleClick);
      cancelAnimationFrame(frameId);
      if (currentMount && renderer.domElement) {
        currentMount.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      shapes.forEach((shape) => {
        shape.geometry.dispose();
        shape.material.dispose();
      });
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={mountRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
};

// ==================== INTERACTIVE CURSOR TRAIL ====================
const CursorTrail = () => {
  const [trails, setTrails] = useState([]);
  const trailId = useRef(0);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const id = trailId.current++;
      const newTrail = {
        id,
        x: e.clientX,
        y: e.clientY,
      };

      setTrails((prev) => [...prev, newTrail].slice(-10));

      setTimeout(() => {
        setTrails((prev) => prev.filter((t) => t.id !== id));
      }, 800);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <>
      {trails.map((trail, index) => (
        <div
          key={trail.id}
          style={{
            position: "fixed",
            left: trail.x,
            top: trail.y,
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: `radial-gradient(circle, rgba(245, 158, 11, ${0.6 - index * 0.06}), transparent)`,
            pointerEvents: "none",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            animation: "fadeOut 0.8s ease-out forwards",
          }}
        />
      ))}
    </>
  );
};

// ==================== PROFESSIONAL CARD ====================
const Card = ({ children, hover = true }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => hover && setIsHovered(true)}
      onMouseLeave={() => hover && setIsHovered(false)}
      style={{
        backgroundColor: "#0f172a",
        border: "1px solid #1e293b",
        borderRadius: "12px",
        transition: "all 0.3s ease",
        borderColor: isHovered ? "#475569" : "#1e293b",
        transform: isHovered ? "translateY(-4px)" : "translateY(0)",
        height: "100%",
      }}
    >
      {children}
    </div>
  );
};

// ==================== NAVBAR ====================
const Navbar = ({ currentPage, setCurrentPage }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = ["Home", "Projects", "About", "Contact"];

  return (
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        transition: "all 0.3s",
        backgroundColor: isScrolled
          ? "rgba(15, 23, 42, 0.95)"
          : "rgba(15, 23, 42, 0.8)",
        backdropFilter: "blur(12px)",
        borderBottom: isScrolled
          ? "1px solid #1e293b"
          : "1px solid transparent",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "1.25rem 2rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            onClick={() => setCurrentPage("Home")}
            style={{
              fontSize: "1.5rem",
              fontWeight: 600,
              color: "#f8fafc",
              border: "none",
              backgroundColor: "transparent",
              letterSpacing: "-0.02em",
              cursor: "pointer",
            }}
          >
            Piyush Mishra
          </button>

          <ul
            style={{
              display: window.innerWidth >= 768 ? "flex" : "none",
              listStyle: "none",
              gap: "2rem",
              alignItems: "center",
              margin: 0,
              padding: 0,
            }}
          >
            {navItems.map((item) => (
              <li key={item}>
                <button
                  onClick={() => setCurrentPage(item)}
                  style={{
                    position: "relative",
                    padding: "0.5rem 0",
                    color: currentPage === item ? "#f8fafc" : "#94a3b8",
                    border: "none",
                    background: "transparent",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "color 0.2s",
                  }}
                >
                  {item}
                  {currentPage === item && (
                    <span
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        backgroundColor: "#f59e0b",
                        borderRadius: "2px",
                      }}
                    />
                  )}
                </button>
              </li>
            ))}
            <li>
              <a
                href="https://my-portfolio-one-chi-91.vercel.app"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: "0.625rem 1.25rem",
                  backgroundColor: "transparent",
                  color: "#f8fafc",
                  border: "1px solid #475569",
                  borderRadius: "8px",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  display: "inline-block",
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = "#f59e0b";
                  e.target.style.backgroundColor = "rgba(245, 158, 11, 0.1)";
                  e.target.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = "#475569";
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                Portfolio 2
              </a>
            </li>
            <li>
              <a
                href="https://pmhunts.github.io/PORTFOLIO/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: "0.625rem 1.25rem",
                  backgroundColor: "#f59e0b",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  boxShadow: "0 0 15px rgba(245, 158, 11, 0.3)",
                  display: "inline-block",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#d97706";
                  e.target.style.boxShadow = "0 0 25px rgba(245, 158, 11, 0.5)";
                  e.target.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#f59e0b";
                  e.target.style.boxShadow = "0 0 15px rgba(245, 158, 11, 0.3)";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                Portfolio V2
              </a>
            </li>
          </ul>

          {window.innerWidth < 768 && (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.35rem",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "0.5rem",
              }}
            >
              <span
                style={{
                  width: "24px",
                  height: "2px",
                  backgroundColor: "#f8fafc",
                  transition: "all 0.3s",
                  transform: isMobileMenuOpen
                    ? "rotate(45deg) translateY(7px)"
                    : "none",
                }}
              />
              <span
                style={{
                  width: "24px",
                  height: "2px",
                  backgroundColor: "#f8fafc",
                  transition: "all 0.3s",
                  opacity: isMobileMenuOpen ? 0 : 1,
                }}
              />
              <span
                style={{
                  width: "24px",
                  height: "2px",
                  backgroundColor: "#f8fafc",
                  transition: "all 0.3s",
                  transform: isMobileMenuOpen
                    ? "rotate(-45deg) translateY(-7px)"
                    : "none",
                }}
              />
            </button>
          )}
        </div>

        {isMobileMenuOpen && window.innerWidth < 768 && (
          <ul
            style={{
              display: "block",
              marginTop: "1.5rem",
              paddingTop: "1.5rem",
              borderTop: "1px solid #1e293b",
              listStyle: "none",
              padding: "1.5rem 0 0 0",
            }}
          >
            {navItems.map((item) => (
              <li key={item} style={{ marginBottom: "1rem" }}>
                <button
                  onClick={() => {
                    setCurrentPage(item);
                    setIsMobileMenuOpen(false);
                  }}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "0.75rem 0",
                    color: currentPage === item ? "#f8fafc" : "#94a3b8",
                    border: "none",
                    background: "transparent",
                    fontSize: "1rem",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  {item}
                </button>
              </li>
            ))}
            <li style={{ marginTop: "1.5rem", marginBottom: "1rem" }}>
              <a
                href="https://my-portfolio-one-chi-91.vercel.app"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  textAlign: "center",
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "transparent",
                  color: "#f8fafc",
                  border: "1px solid #475569",
                  borderRadius: "8px",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                Portfolio 2
              </a>
            </li>
            <li style={{ marginBottom: "1rem" }}>
              <a
                href="https://pmhunts.github.io/PORTFOLIO/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  textAlign: "center",
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "#f59e0b",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  boxShadow: "0 0 15px rgba(245, 158, 11, 0.3)",
                }}
              >
                Portfolio V2
              </a>
            </li>
          </ul>
        )}
      </div>
    </nav>
  );
};

// ==================== HOME PAGE ====================
const Home = ({ setCurrentPage }) => {
  const [visibleSkills, setVisibleSkills] = useState([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const skills = [
    { name: "React & Next.js", level: 90 },
    { name: "TypeScript", level: 85 },
    { name: "Node.js & Express", level: 88 },
    { name: "CSS & Tailwind", level: 92 },
    { name: "Three.js & WebGL", level: 80 },
    { name: "MongoDB & SQL", level: 85 },
  ];

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setVisibleSkills((prev) => [...new Set([...prev, index])]);
          }
        });
      },
      { threshold: 0.1 },
    );

    document
      .querySelectorAll(".skill-item")
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div style={{ minHeight: "100vh" }}>
      <section
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          padding: "8rem 2rem 4rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            width: "100%",
            transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
            transition: "transform 0.3s ease-out",
          }}
        >
          <div style={{ maxWidth: "800px" }}>
            <p
              style={{
                color: "#f59e0b",
                fontSize: "0.95rem",
                fontWeight: 600,
                letterSpacing: "0.1em",
                marginBottom: "1rem",
                textTransform: "uppercase",
              }}
            >
              Full Stack Developer
            </p>
            <h1
              style={{
                fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
                fontWeight: 700,
                color: "#f8fafc",
                lineHeight: 1.1,
                marginBottom: "1.5rem",
                letterSpacing: "-0.02em",
              }}
            >
              Building Digital Experiences That Matter
            </h1>
            <p
              style={{
                color: "#94a3b8",
                fontSize: "1.125rem",
                lineHeight: 1.8,
                marginBottom: "2.5rem",
                maxWidth: "600px",
              }}
            >
              Specialized in creating modern web applications with React,
              Node.js, and AI integration. Currently pursuing Computer Science
              with AI at PSIT, Kanpur.
            </p>
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <button
                onClick={() => setCurrentPage("Projects")}
                style={{
                  padding: "0.875rem 2rem",
                  backgroundColor: "#f59e0b",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  boxShadow: "0 0 20px rgba(245, 158, 11, 0.3)",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#d97706";
                  e.target.style.boxShadow = "0 0 30px rgba(245, 158, 11, 0.5)";
                  e.target.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#f59e0b";
                  e.target.style.boxShadow = "0 0 20px rgba(245, 158, 11, 0.3)";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                View Projects
              </button>
              <button
                onClick={() => setCurrentPage("Contact")}
                style={{
                  padding: "0.875rem 2rem",
                  backgroundColor: "transparent",
                  color: "#f8fafc",
                  border: "2px solid #475569",
                  borderRadius: "8px",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = "#f59e0b";
                  e.target.style.backgroundColor = "rgba(245, 158, 11, 0.1)";
                  e.target.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = "#475569";
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                Get In Touch
              </button>
              <a
                href="https://drive.google.com/uc?export=download&id=17s9OSC8b2IvaBmuWFFZ4n4Aa7hfEW06Q"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0.875rem 2rem",
                  backgroundColor: "transparent",
                  color: "#f59e0b",
                  border: "2px solid #f59e0b",
                  borderRadius: "8px",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#f59e0b";
                  e.target.style.color = "#ffffff";
                  e.target.style.boxShadow = "0 0 25px rgba(245, 158, 11, 0.4)";
                  e.target.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = "#f59e0b";
                  e.target.style.boxShadow = "none";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                📄 Download Resume
              </a>
            </div>
          </div>
        </div>
      </section>

      <section
        style={{
          padding: "6rem 2rem",
          backgroundColor: "rgba(15, 23, 42, 0.5)",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <h2
            style={{
              fontSize: "clamp(2rem, 4vw, 2.5rem)",
              fontWeight: 700,
              color: "#f8fafc",
              marginBottom: "3rem",
              letterSpacing: "-0.02em",
            }}
          >
            Technical Expertise
          </h2>
          <div style={{ maxWidth: "700px" }}>
            {skills.map((skill, index) => (
              <div
                key={index}
                data-index={index}
                className="skill-item"
                style={{
                  marginBottom: "2rem",
                  opacity: visibleSkills.includes(index) ? 1 : 0,
                  transform: visibleSkills.includes(index)
                    ? "translateY(0)"
                    : "translateY(1rem)",
                  transition: "all 0.6s ease-out",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "0.75rem",
                  }}
                >
                  <span
                    style={{
                      color: "#e2e8f0",
                      fontWeight: 500,
                      fontSize: "0.95rem",
                    }}
                  >
                    {skill.name}
                  </span>
                  <span style={{ color: "#64748b", fontSize: "0.9rem" }}>
                    {skill.level}%
                  </span>
                </div>
                <div
                  style={{
                    height: "8px",
                    backgroundColor: "#1e293b",
                    borderRadius: "4px",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      background: "linear-gradient(90deg, #f59e0b, #fbbf24)",
                      borderRadius: "4px",
                      transition: "width 1s ease-out",
                      width: visibleSkills.includes(index)
                        ? `${skill.level}%`
                        : "0%",
                      boxShadow: visibleSkills.includes(index)
                        ? "0 0 10px rgba(245, 158, 11, 0.5)"
                        : "none",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// ==================== PROJECTS PAGE ====================
const Projects = () => {
  const [filter, setFilter] = useState("all");

  const projects = [
    {
      id: 1,
      title: "AI Customer Platform",
      description:
        "Comprehensive AI-powered customer support platform with real-time analytics and intelligent chatbot integration.",
      tags: ["React", "Node.js", "AI", "WebRTC"],
      category: "fullstack",
      demoLink: "https://pmhunts.github.io/CUSTOMER_SERVICE-AI-PLATFORM-/",
      githubLink: "https://github.com/pmhunts/CUSTOMER_SERVICE-AI-PLATFORM-",
    },
    {
      id: 2,
      title: "Multi-Model RAG System",
      description:
        "Sophisticated Retrieval-Augmented Generation system with multiple AI models for enhanced document processing.",
      tags: ["AI", "RAG", "Streamlit", "Python"],
      category: "fullstack",
      demoLink:
        "https://multi-model-rag-system-o3aejyczh7cm4riacxnfjw.streamlit.app/",
      githubLink: "https://github.com/pmhunts/multi-model-rag-system",
    },
    {
      id: 3,
      title: "SecureVault Password Manager",
      description:
        "Enterprise-grade password manager with bcrypt encryption and JWT authentication for secure credential storage.",
      tags: ["MongoDB", "Express", "JWT", "bcrypt"],
      category: "fullstack",
      demoLink: "https://password-manager-react-lovat.vercel.app/",
      githubLink: "https://github.com/pmhunts/SecureVault-Password-Manager",
    },
    {
      id: 4,
      title: "Branch Support Portal",
      description:
        "Professional branch management system with comprehensive CRUD operations and role-based access control.",
      tags: ["React", "Node.js", "MongoDB"],
      category: "web",
      demoLink: "https://branch-support-portal.vercel.app/",
      githubLink: "https://github.com/pmhunts/branch-support-portal",
    },
    {
      id: 5,
      title: "Emergency Alert System",
      description:
        "Real-time emergency alert system with location tracking and instant messaging capabilities.",
      tags: ["React", "Node.js", "Real-time"],
      category: "fullstack",
      demoLink: "https://emergency-alert-sooty.vercel.app/",
      githubLink: "https://github.com/pmhunts/emergency-alert",
    },
    {
      id: 6,
      title: "Real-Time Edge Detection",
      description:
        "Advanced computer vision application featuring real-time edge detection using Canny algorithm with live camera feed processing.",
      tags: ["Python", "OpenCV", "Computer Vision", "Real-time"],
      category: "fullstack",
      demoLink: "https://real-time-edge-detection-mocha.vercel.app/",
      githubLink: "https://github.com/pmhunts/real-time-edge-detection",
    },
  ];

  const categories = ["all", "web", "fullstack"];
  const filteredProjects =
    filter === "all" ? projects : projects.filter((p) => p.category === filter);

  return (
    <div style={{ minHeight: "100vh", paddingTop: "6rem" }}>
      <div
        style={{ maxWidth: "1200px", margin: "0 auto", padding: "4rem 2rem" }}
      >
        <h1
          style={{
            fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
            fontWeight: 700,
            color: "#f8fafc",
            marginBottom: "1rem",
            letterSpacing: "-0.02em",
          }}
        >
          Featured Projects
        </h1>
        <p
          style={{
            color: "#94a3b8",
            fontSize: "1.125rem",
            marginBottom: "3rem",
            maxWidth: "600px",
          }}
        >
          A selection of recent work showcasing full-stack development and AI
          integration
        </p>

        <div
          style={{
            display: "flex",
            gap: "1rem",
            marginBottom: "3rem",
            flexWrap: "wrap",
          }}
        >
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              style={{
                padding: "0.625rem 1.5rem",
                borderRadius: "8px",
                fontWeight: 500,
                fontSize: "0.9rem",
                transition: "all 0.2s",
                border: "none",
                cursor: "pointer",
                backgroundColor: filter === cat ? "#f59e0b" : "#1e293b",
                color: filter === cat ? "#ffffff" : "#94a3b8",
                boxShadow:
                  filter === cat ? "0 0 15px rgba(245, 158, 11, 0.3)" : "none",
              }}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              window.innerWidth >= 1024 ? "repeat(2, 1fr)" : "1fr",
            gap: "2rem",
          }}
        >
          {filteredProjects.map((project) => (
            <Card key={project.id}>
              <div style={{ padding: "2rem" }}>
                <h3
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: "#f8fafc",
                    marginBottom: "0.75rem",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {project.title}
                </h3>
                <p
                  style={{
                    color: "#94a3b8",
                    lineHeight: 1.7,
                    marginBottom: "1.5rem",
                    fontSize: "0.95rem",
                  }}
                >
                  {project.description}
                </p>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                    marginBottom: "1.5rem",
                  }}
                >
                  {project.tags.map((tag, i) => (
                    <span
                      key={i}
                      style={{
                        padding: "0.375rem 0.875rem",
                        backgroundColor: "#1e293b",
                        borderRadius: "6px",
                        fontSize: "0.8rem",
                        color: "#cbd5e1",
                        fontWeight: 500,
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: "1rem" }}>
                  <a
                    href={project.demoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: "0.75rem 1.5rem",
                      backgroundColor: "#f59e0b",
                      color: "#ffffff",
                      borderRadius: "8px",
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      transition: "all 0.2s",
                      cursor: "pointer",
                      boxShadow: "0 0 15px rgba(245, 158, 11, 0.3)",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#d97706";
                      e.target.style.boxShadow =
                        "0 0 25px rgba(245, 158, 11, 0.5)";
                      e.target.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#f59e0b";
                      e.target.style.boxShadow =
                        "0 0 15px rgba(245, 158, 11, 0.3)";
                      e.target.style.transform = "translateY(0)";
                    }}
                  >
                    Live Demo
                  </a>
                  <a
                    href={project.githubLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: "0.75rem 1.5rem",
                      backgroundColor: "transparent",
                      color: "#f8fafc",
                      border: "1px solid #475569",
                      borderRadius: "8px",
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      transition: "all 0.2s",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = "#f59e0b";
                      e.target.style.backgroundColor =
                        "rgba(245, 158, 11, 0.1)";
                      e.target.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = "#475569";
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.transform = "translateY(0)";
                    }}
                  >
                    GitHub
                  </a>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== ABOUT PAGE ====================
const About = () => {
  const stats = [
    { value: "8.2", label: "CGPA" },
    { value: "20+", label: "Projects" },
    { value: "2+", label: "Internships" },
  ];

  const experience = [
    {
      title: "AI Trainee",
      company: "Ashna AI",
      period: "May 2025 - Jun 2025",
      description:
        "Developed expertise in AI-powered solutions, NLP, and generative models. Worked on implementing machine learning algorithms for real-world applications.",
    },
    {
      title: "Software Developer Intern",
      company: "Fintech Company",
      period: "Feb 2025 - Apr 2025",
      description:
        "Built responsive IPO management system using React and Node.js with Agile methodology. Collaborated with cross-functional teams to deliver scalable solutions.",
    },
  ];

  return (
    <div style={{ minHeight: "100vh", paddingTop: "6rem" }}>
      <div
        style={{ maxWidth: "1200px", margin: "0 auto", padding: "4rem 2rem" }}
      >
        <h1
          style={{
            fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
            fontWeight: 700,
            color: "#f8fafc",
            marginBottom: "1rem",
            letterSpacing: "-0.02em",
          }}
        >
          About Me
        </h1>
        <p
          style={{
            color: "#94a3b8",
            fontSize: "1.125rem",
            marginBottom: "4rem",
            maxWidth: "600px",
          }}
        >
          Passionate about creating impactful digital solutions through code
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: window.innerWidth >= 768 ? "1fr 1fr" : "1fr",
            gap: "3rem",
            marginBottom: "5rem",
          }}
        >
          <div>
            <div
              style={{
                width: "280px",
                height: "280px",
                margin: "0 auto 2rem",
                marginLeft: "2rem",
                borderRadius: "16px",
                overflow: "hidden",
                border: "3px solid #f59e0b",
                boxShadow: "0 0 30px rgba(245, 158, 11, 0.3)",
                position: "relative",
                background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
              }}
            >
              {/* Replace this with your actual photo */}
              <img
                src="/piyush_photograph.jpg"
                alt="Piyush Mishra"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              {/* Golden corner accent */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "60px",
                  height: "60px",
                  background:
                    "linear-gradient(135deg, transparent 50%, rgba(245, 158, 11, 0.3) 50%)",
                }}
              />
            </div>

            <h2
              style={{
                fontSize: "1.75rem",
                fontWeight: 600,
                color: "#f8fafc",
                marginBottom: "1.5rem",
                letterSpacing: "-0.01em",
              }}
            >
              Background
            </h2>
            <p
              style={{
                color: "#94a3b8",
                lineHeight: 1.8,
                marginBottom: "1.25rem",
                fontSize: "0.975rem",
              }}
            >
              I'm a Computer Science student specializing in AI at Pranveer
              Singh Institute of Technology, Kanpur. With a strong foundation in
              full-stack development and emerging AI technologies, I bring
              creativity and technical expertise to every project.
            </p>
            <p
              style={{
                color: "#94a3b8",
                lineHeight: 1.8,
                marginBottom: "1.25rem",
                fontSize: "0.975rem",
              }}
            >
              My journey spans from developing responsive web applications using
              modern frameworks like React and Next.js to exploring the
              frontiers of artificial intelligence. I've gained practical
              experience through internships in fintech and AI, where I've
              contributed to real-world solutions.
            </p>
            <p
              style={{
                color: "#94a3b8",
                lineHeight: 1.8,
                fontSize: "0.975rem",
              }}
            >
              Currently maintaining an 8.2 CGPA while actively building projects
              that solve real problems. I'm always eager to learn new
              technologies and take on challenging opportunities.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "1rem",
            }}
          >
            {stats.map((stat, i) => (
              <Card key={i} hover={false}>
                <div style={{ padding: "2rem 1rem", textAlign: "center" }}>
                  <h3
                    style={{
                      fontSize: "2.25rem",
                      fontWeight: 700,
                      color: "#f59e0b",
                      marginBottom: "0.5rem",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {stat.value}
                  </h3>
                  <p
                    style={{
                      color: "#94a3b8",
                      fontSize: "0.85rem",
                      fontWeight: 500,
                    }}
                  >
                    {stat.label}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>

        <div>
          <h2
            style={{
              fontSize: "1.75rem",
              fontWeight: 600,
              color: "#f8fafc",
              marginBottom: "2rem",
              letterSpacing: "-0.01em",
            }}
          >
            Experience
          </h2>
          <div style={{ maxWidth: "800px" }}>
            {experience.map((exp, i) => (
              <div key={i} style={{ marginBottom: "2rem" }}>
                <Card>
                  <div style={{ padding: "2rem" }}>
                    <div style={{ marginBottom: "0.75rem" }}>
                      <h3
                        style={{
                          fontSize: "1.25rem",
                          fontWeight: 600,
                          color: "#f8fafc",
                          marginBottom: "0.5rem",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {exp.title}
                      </h3>
                      <div
                        style={{
                          display: "flex",
                          gap: "1rem",
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            color: "#f59e0b",
                            fontSize: "0.9rem",
                            fontWeight: 600,
                          }}
                        >
                          {exp.company}
                        </span>
                        <span
                          style={{
                            color: "#64748b",
                            fontSize: "0.85rem",
                          }}
                        >
                          {exp.period}
                        </span>
                      </div>
                    </div>
                    <p
                      style={{
                        color: "#94a3b8",
                        lineHeight: 1.7,
                        fontSize: "0.95rem",
                      }}
                    >
                      {exp.description}
                    </p>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== CONTACT PAGE ====================
//
// IMPORTANT: TO MAKE EMAIL SENDING WORK, FOLLOW THESE STEPS:
//
// OPTION 1 - Web3Forms (Easiest, Recommended):
// 1. Go to https://web3forms.com/
// 2. Enter your email: 2k22.csai.2213601@gmail.com
// 3. You'll receive an access key via email
// 4. Replace the demo access_key in handleSubmit with your real key
// 5. Done! Emails will be sent directly to your Gmail
//
// OPTION 2 - Formspree:
// 1. Go to https://formspree.io/
// 2. Sign up with your email
// 3. Create a new form
// 4. Get your form endpoint (e.g., https://formspree.io/f/YOUR_FORM_ID)
// 5. Replace the fetch URL in handleSubmit with your endpoint
//
// OPTION 3 - EmailJS:
// 1. Go to https://www.emailjs.com/
// 2. Sign up and add your email service (Gmail)
// 3. Create an email template
// 4. Get your Service ID, Template ID, and Public Key
// 5. Use EmailJS SDK in the handleSubmit function
//
const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [location, setLocation] = useState("Detecting location...");

  useEffect(() => {
    const detectLocation = async () => {
      try {
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();
        setLocation(`${data.city}, ${data.region}, ${data.country_name}`);
      } catch (error) {
        setLocation("Location unavailable");
      }
    };
    detectLocation();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Method 1: Using Formspree with correct endpoint
      // First, you need to sign up at https://formspree.io/ and verify your email
      // Then replace 'YOUR_FORM_ID' with your actual form ID from Formspree dashboard

      // Method 2: Using Web3Forms (Alternative - works immediately)
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: "474421e4-f3af-47c0-9deb-f70d0bdbacc0", // Your real Web3Forms access key
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          location: location,
          from_name: formData.name,
          to_email: "2k22.csai.2213601@gmail.com",
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", subject: "", message: "" });
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        setSubmitStatus("error");
        setTimeout(() => setSubmitStatus(null), 5000);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setSubmitStatus("error");
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      label: "Email",
      value: "2k22.csai.2213601@gmail.com",
      link: "mailto:2k22.csai.2213601@gmail.com",
    },
    { label: "Phone", value: "+91 7666243168", link: "tel:+917666243168" },
    { label: "Location", value: location, link: null },
  ];

  return (
    <div style={{ minHeight: "100vh", paddingTop: "6rem" }}>
      <div
        style={{ maxWidth: "1200px", margin: "0 auto", padding: "4rem 2rem" }}
      >
        <h1
          style={{
            fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
            fontWeight: 700,
            color: "#f8fafc",
            marginBottom: "1rem",
            letterSpacing: "-0.02em",
          }}
        >
          Get In Touch
        </h1>
        <p
          style={{
            color: "#94a3b8",
            fontSize: "1.125rem",
            marginBottom: "1rem",
            maxWidth: "600px",
          }}
        >
          Let's discuss your next project or opportunity
        </p>
        <p
          style={{
            color: "#64748b",
            fontSize: "0.95rem",
            marginBottom: "4rem",
            maxWidth: "600px",
          }}
        >
          Fill out the form below and I'll get back to you within 24 hours. Your
          message will be sent directly to my email: 2k22.csai.2213601@gmail.com
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: window.innerWidth >= 768 ? "1fr 1fr" : "1fr",
            gap: "3rem",
          }}
        >
          <div>
            <div style={{ marginBottom: "2rem" }}>
              {contactInfo.map((info, i) => (
                <div key={i} style={{ marginBottom: "1.5rem" }}>
                  <h4
                    style={{
                      color: "#cbd5e1",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      marginBottom: "0.5rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {info.label}
                  </h4>
                  {info.link ? (
                    <a
                      href={info.link}
                      style={{
                        color: "#f8fafc",
                        textDecoration: "none",
                        fontSize: "1.05rem",
                        transition: "color 0.2s",
                      }}
                    >
                      {info.value}
                    </a>
                  ) : (
                    <p style={{ color: "#f8fafc", fontSize: "1.05rem" }}>
                      {info.value}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <a
                href="https://github.com/pmhunts"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "#1e293b",
                  color: "#f8fafc",
                  border: "1px solid #334155",
                  borderRadius: "8px",
                  textDecoration: "none",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  transition: "all 0.2s",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = "#f59e0b";
                  e.target.style.backgroundColor = "rgba(245, 158, 11, 0.1)";
                  e.target.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = "#334155";
                  e.target.style.backgroundColor = "#1e293b";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                GitHub
              </a>
              <a
                href="https://www.linkedin.com/in/piyush-mishra-pmhunts/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "#1e293b",
                  color: "#f8fafc",
                  border: "1px solid #334155",
                  borderRadius: "8px",
                  textDecoration: "none",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  transition: "all 0.2s",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = "#f59e0b";
                  e.target.style.backgroundColor = "rgba(245, 158, 11, 0.1)";
                  e.target.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = "#334155";
                  e.target.style.backgroundColor = "#1e293b";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                LinkedIn
              </a>
            </div>
          </div>

          <Card hover={false}>
            <div style={{ padding: "2rem" }}>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: "1.5rem" }}>
                  <label
                    style={{
                      display: "block",
                      color: "#cbd5e1",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      marginBottom: "0.5rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    style={{
                      width: "100%",
                      padding: "0.875rem 1rem",
                      backgroundColor: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: "8px",
                      color: "#f8fafc",
                      fontSize: "0.95rem",
                      outline: "none",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#f59e0b")}
                    onBlur={(e) => (e.target.style.borderColor = "#334155")}
                  />
                </div>

                <div style={{ marginBottom: "1.5rem" }}>
                  <label
                    style={{
                      display: "block",
                      color: "#cbd5e1",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      marginBottom: "0.5rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    style={{
                      width: "100%",
                      padding: "0.875rem 1rem",
                      backgroundColor: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: "8px",
                      color: "#f8fafc",
                      fontSize: "0.95rem",
                      outline: "none",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#f59e0b")}
                    onBlur={(e) => (e.target.style.borderColor = "#334155")}
                  />
                </div>

                <div style={{ marginBottom: "1.5rem" }}>
                  <label
                    style={{
                      display: "block",
                      color: "#cbd5e1",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      marginBottom: "0.5rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    style={{
                      width: "100%",
                      padding: "0.875rem 1rem",
                      backgroundColor: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: "8px",
                      color: "#f8fafc",
                      fontSize: "0.95rem",
                      outline: "none",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#f59e0b")}
                    onBlur={(e) => (e.target.style.borderColor = "#334155")}
                  />
                </div>

                <div style={{ marginBottom: "1.5rem" }}>
                  <label
                    style={{
                      display: "block",
                      color: "#cbd5e1",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      marginBottom: "0.5rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    rows="5"
                    style={{
                      width: "100%",
                      padding: "0.875rem 1rem",
                      backgroundColor: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: "8px",
                      color: "#f8fafc",
                      fontSize: "0.95rem",
                      outline: "none",
                      resize: "vertical",
                      transition: "border-color 0.2s",
                      fontFamily: "inherit",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#f59e0b")}
                    onBlur={(e) => (e.target.style.borderColor = "#334155")}
                  />
                </div>

                {submitStatus === "success" && (
                  <div
                    style={{
                      padding: "1rem",
                      backgroundColor: "rgba(34, 197, 94, 0.1)",
                      border: "1px solid #22c55e",
                      borderRadius: "8px",
                      color: "#22c55e",
                      marginBottom: "1.5rem",
                      fontSize: "0.9rem",
                      textAlign: "center",
                    }}
                  >
                    ✓ Message sent successfully! I'll get back to you soon.
                  </div>
                )}

                {submitStatus === "error" && (
                  <div
                    style={{
                      padding: "1rem",
                      backgroundColor: "rgba(239, 68, 68, 0.1)",
                      border: "1px solid #ef4444",
                      borderRadius: "8px",
                      color: "#ef4444",
                      marginBottom: "1.5rem",
                      fontSize: "0.9rem",
                      textAlign: "center",
                    }}
                  >
                    ✗ Failed to send message. Please try emailing directly at
                    2k22.csai.2213601@gmail.com
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    width: "100%",
                    padding: "0.875rem 2rem",
                    backgroundColor: "#f59e0b",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    opacity: isSubmitting ? 0.6 : 1,
                    boxShadow: "0 0 20px rgba(245, 158, 11, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.target.style.backgroundColor = "#d97706";
                      e.target.style.boxShadow =
                        "0 0 30px rgba(245, 158, 11, 0.5)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#f59e0b";
                    e.target.style.boxShadow =
                      "0 0 20px rgba(245, 158, 11, 0.3)";
                  }}
                >
                  {isSubmitting ? "Sending Message..." : "Send Message"}
                </button>

                <p
                  style={{
                    marginTop: "1rem",
                    fontSize: "0.8rem",
                    color: "#64748b",
                    textAlign: "center",
                  }}
                >
                  Your message will be sent to 2k22.csai.2213601@gmail.com
                </p>
              </form>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ==================== FOOTER ====================
const Footer = () => {
  return (
    <footer
      style={{
        backgroundColor: "#0f172a",
        borderTop: "1px solid #1e293b",
        padding: "3rem 2rem",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              window.innerWidth >= 768 ? "repeat(3, 1fr)" : "1fr",
            gap: "2rem",
            marginBottom: "2rem",
          }}
        >
          <div>
            <h3
              style={{
                fontSize: "1.25rem",
                fontWeight: 600,
                color: "#f8fafc",
                marginBottom: "0.75rem",
                letterSpacing: "-0.01em",
              }}
            >
              Piyush Mishra
            </h3>
            <p style={{ color: "#64748b", fontSize: "0.9rem" }}>
              Full Stack Developer & AI Enthusiast
            </p>
          </div>

          <div>
            <h4
              style={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: "#cbd5e1",
                marginBottom: "1rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Quick Links
            </h4>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {["Home", "Projects", "About", "Contact"].map((item) => (
                <li key={item} style={{ marginBottom: "0.5rem" }}>
                  <button
                    onClick={() => {}}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      color: "#94a3b8",
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      transition: "color 0.2s",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4
              style={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: "#cbd5e1",
                marginBottom: "1rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Connect
            </h4>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <a
                href="https://github.com/pmhunts"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#94a3b8",
                  textDecoration: "none",
                  fontSize: "0.9rem",
                  transition: "color 0.2s",
                }}
              >
                GitHub
              </a>
              <a
                href="https://www.linkedin.com/in/piyush-mishra-pmhunts/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#94a3b8",
                  textDecoration: "none",
                  fontSize: "0.9rem",
                  transition: "color 0.2s",
                }}
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>

        <div
          style={{
            paddingTop: "2rem",
            borderTop: "1px solid #1e293b",
            textAlign: "center",
          }}
        >
          <p style={{ color: "#475569", fontSize: "0.85rem" }}>
            © 2025 Piyush Mishra. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// ==================== HACKER INTRO SCREEN ====================
const HackerIntro = ({ onFinish }) => {
  const canvasRef = useRef(null);
  const [terminalLines, setTerminalLines] = useState([]);
  const [showCursor, setShowCursor] = useState(true);
  const [exitPhase, setExitPhase] = useState(0); // 0 = idle, 1 = fading out

  const lines = [
    { text: "> Establishing encrypted connection...", delay: 0 },
    { text: "> ██████████░░░░░░░░░░ 45%", delay: 380 },
    { text: "> ████████████████░░░░ 78%", delay: 600 },
    { text: "> ██████████████████████ 100%", delay: 820 },
    { text: "> Connection secured.", delay: 1000 },
    { text: "> Verifying identity...", delay: 1350 },
    { text: "> Identity confirmed.", delay: 1700 },
    { text: "", delay: 1900 },
    { text: "> ─────────────────────────────────────", delay: 1900 },
    { text: "> PIYUSH MISHRA", delay: 2150 },
    { text: "> Full Stack Developer & AI Engineer", delay: 2350 },
    { text: "> ─────────────────────────────────────", delay: 2450 },
    { text: "", delay: 2700 },
    { text: "> [ Click anywhere to continue ]", delay: 2700 },
  ];

  // Matrix rain — subtle gold, slow drift
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const fontSize = 14;
    const columns = Math.floor(canvas.width / fontSize);
    const drops = Array(columns)
      .fill(0)
      .map(() => Math.random() * -60);
    const chars = "01アイウエオカキクケコサシスセソタチツテト{}[]<>/\\|";

    let frameId;
    const draw = () => {
      ctx.fillStyle = "rgba(2, 6, 23, 0.07)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < drops.length; i++) {
        const char = chars[Math.floor(Math.random() * chars.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;

        // Head char is bright, trail fades
        ctx.fillStyle =
          y === drops[i] * fontSize
            ? "rgba(245, 158, 11, 0.85)"
            : "rgba(245, 158, 11, 0.3)";
        ctx.font = `${fontSize}px monospace`;
        ctx.fillText(char, x, y);

        if (y > canvas.height && Math.random() > 0.965) {
          drops[i] = Math.random() * -30;
        }
        drops[i] += 0.7 + Math.random() * 0.3;
      }
      frameId = requestAnimationFrame(draw);
    };
    draw();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Cursor blink
  useEffect(() => {
    const interval = setInterval(() => setShowCursor((c) => !c), 530);
    return () => clearInterval(interval);
  }, []);

  // Type out lines
  useEffect(() => {
    const timeouts = [];
    lines.forEach((line) => {
      const t = setTimeout(() => {
        setTerminalLines((prev) => [...prev, line.text]);
      }, line.delay);
      timeouts.push(t);
    });
    return () => timeouts.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Smooth fade-out exit
  useEffect(() => {
    if (exitPhase === 1) {
      const t = setTimeout(() => onFinish(), 450);
      return () => clearTimeout(t);
    }
  }, [exitPhase, onFinish]);

  const handleClick = () => {
    if (exitPhase === 0 && terminalLines.length >= lines.length) {
      setExitPhase(1);
    }
  };

  const allDone = terminalLines.length >= lines.length;

  return (
    <div
      onClick={handleClick}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 9999,
        backgroundColor: "#020617",
        overflow: "hidden",
        cursor: allDone && exitPhase === 0 ? "pointer" : "default",
        opacity: exitPhase === 1 ? 0 : 1,
        transition: "opacity 0.5s ease-out",
      }}
    >
      {/* Matrix Canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity: exitPhase === 1 ? 0 : 0.3,
          transition: "opacity 0.4s ease-out",
        }}
      />

      {/* Very subtle scanlines */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          background:
            "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.06) 3px, rgba(0,0,0,0.06) 4px)",
          zIndex: 2,
        }}
      />

      {/* Single slow gold sweep line */}
      <div
        style={{
          position: "absolute",
          left: 0,
          width: "100%",
          height: "1px",
          background:
            "linear-gradient(90deg, transparent 0%, rgba(245,158,11,0.2) 50%, transparent 100%)",
          zIndex: 3,
          animation: "scanSweep 5s linear infinite",
          opacity: exitPhase === 1 ? 0 : 1,
          transition: "opacity 0.3s",
        }}
      />

      {/* Terminal window */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "580px",
          zIndex: 5,
          opacity: exitPhase === 1 ? 0 : 1,
          transition: "opacity 0.45s ease-out",
        }}
      >
        {/* macOS title bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            padding: "9px 14px",
            backgroundColor: "#0d1117",
            borderRadius: "8px 8px 0 0",
            border: "1px solid #1e293b",
            borderBottom: "none",
          }}
        >
          <div
            style={{
              width: "11px",
              height: "11px",
              borderRadius: "50%",
              backgroundColor: "#ff5f57",
            }}
          />
          <div
            style={{
              width: "11px",
              height: "11px",
              borderRadius: "50%",
              backgroundColor: "#ffbd2e",
            }}
          />
          <div
            style={{
              width: "11px",
              height: "11px",
              borderRadius: "50%",
              backgroundColor: "#28ca41",
            }}
          />
          <span
            style={{
              marginLeft: "auto",
              marginRight: "auto",
              color: "#4b5563",
              fontSize: "0.72rem",
              fontFamily: "monospace",
              letterSpacing: "0.03em",
            }}
          >
            piyush@portfolio ~ bash
          </span>
        </div>

        {/* Terminal body */}
        <div
          style={{
            backgroundColor: "#0a0e1c",
            border: "1px solid #1e293b",
            borderRadius: "0 0 8px 8px",
            padding: "20px 24px 24px",
            minHeight: "240px",
            boxShadow: "0 0 40px rgba(245,158,11,0.07)",
          }}
        >
          {terminalLines.map((line, i) => {
            const isName = line.includes("PIYUSH MISHRA");
            const isRole = line.includes("Full Stack");
            const isSuccess =
              line.includes("100%") ||
              line.includes("confirmed") ||
              line.includes("secured");
            const isDivider = line.includes("─────");
            const isEnter = line.includes("Click anywhere");
            const isEmpty = line === "";

            return (
              <div
                key={i}
                style={{
                  fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
                  fontSize: isName ? "1.1rem" : "0.8rem",
                  lineHeight: isEmpty ? "0.5" : "2.1",
                  color: isName
                    ? "#f59e0b"
                    : isRole
                      ? "#c48a3a"
                      : isSuccess
                        ? "#4ade80"
                        : isDivider
                          ? "#1e293b"
                          : isEnter
                            ? "#94a3b8"
                            : "#5a6577",
                  fontWeight: isName ? 700 : 400,
                  letterSpacing: isName ? "0.1em" : "0.01em",
                  textShadow: isName ? "0 0 14px rgba(245,158,11,0.4)" : "none",
                  animation: isEnter
                    ? "enterPulse 2.2s ease-in-out infinite"
                    : "none",
                }}
              >
                {line}
              </div>
            );
          })}

          {/* Blinking cursor while still typing */}
          {!allDone && (
            <span
              style={{
                fontFamily: "monospace",
                fontSize: "0.8rem",
                color: "#f59e0b",
                opacity: showCursor ? 1 : 0,
                transition: "opacity 0.1s",
              }}
            >
              █
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
// ==================== PORTAL TRANSITION ====================
const PortalTransition = ({ onDone }) => {
  const canvasRef = useRef(null);
  const [phase, setPhase] = useState(0);
  // phase 0 = white flash
  // phase 1 = "ENTERING..." text + particle vortex
  // phase 2 = warp lines zoom
  // phase 3 = final fade to portfolio

  useEffect(() => {
    // phase 0 → 1 after flash
    const t0 = setTimeout(() => setPhase(1), 350);
    // phase 1 → 2 after vortex
    const t1 = setTimeout(() => setPhase(2), 1200);
    // phase 2 → 3 after warp
    const t2 = setTimeout(() => setPhase(3), 2000);
    // call done
    const t3 = setTimeout(() => onDone(), 2500);
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [onDone]);

  // Vortex + warp particle canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    // Build particles: rings of dots that spiral inward
    const particles = [];
    for (let i = 0; i < 180; i++) {
      const angle = (i / 180) * Math.PI * 2;
      const ring = Math.floor(i / 30); // 0-5
      particles.push({
        x: cx + Math.cos(angle) * (120 + ring * 80),
        y: cy + Math.sin(angle) * (120 + ring * 80),
        angle: angle,
        dist: 120 + ring * 80,
        speed: 0.04 + ring * 0.015,
        size: 2 + Math.random() * 2,
        alpha: 0.3 + Math.random() * 0.5,
        hue: 38 + Math.random() * 12, // gold range
      });
    }

    // Warp lines: radial lines that zoom outward in phase 2
    const warpLines = [];
    for (let i = 0; i < 40; i++) {
      warpLines.push({
        angle: (i / 40) * Math.PI * 2,
        progress: Math.random(), // stagger start
        speed: 0.6 + Math.random() * 0.8,
      });
    }

    let startTime = performance.now();
    let frameId;

    const draw = () => {
      const elapsed = (performance.now() - startTime) / 1000; // seconds
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // --- PHASE 1: vortex swirl ---
      if (phase >= 1 && phase <= 2) {
        particles.forEach((p) => {
          // Spiral inward
          p.angle += p.speed * 0.08;
          p.dist *= 0.985;

          const x = cx + Math.cos(p.angle) * p.dist;
          const y = cy + Math.sin(p.angle) * p.dist;

          // Fade out as they reach center
          const fadeDist = p.dist / 40;
          const a = Math.min(p.alpha, fadeDist) * (phase === 2 ? 0.4 : 1);

          ctx.beginPath();
          ctx.arc(x, y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = `hsla(${p.hue}, 85%, 55%, ${a})`;
          ctx.fill();
        });
      }

      // --- PHASE 2: warp speed lines zooming out from center ---
      if (phase >= 2) {
        const warpAlpha =
          phase === 3 ? Math.max(0, 1 - (elapsed - 0.8) * 2) : 0.7;
        warpLines.forEach((line) => {
          line.progress += line.speed * 0.016;
          if (line.progress > 1) line.progress = 0;

          const innerR = line.progress * 60;
          const outerR =
            line.progress * Math.max(canvas.width, canvas.height) * 0.8;

          const x1 = cx + Math.cos(line.angle) * innerR;
          const y1 = cy + Math.sin(line.angle) * innerR;
          const x2 = cx + Math.cos(line.angle) * outerR;
          const y2 = cy + Math.sin(line.angle) * outerR;

          const grad = ctx.createLinearGradient(x1, y1, x2, y2);
          grad.addColorStop(0, `rgba(245, 158, 11, ${warpAlpha * 0.9})`);
          grad.addColorStop(0.4, `rgba(245, 158, 11, ${warpAlpha * 0.5})`);
          grad.addColorStop(1, "rgba(245, 158, 11, 0)");

          ctx.beginPath();
          ctx.strokeStyle = grad;
          ctx.lineWidth = 1.5;
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
        });
      }

      frameId = requestAnimationFrame(draw);
    };
    draw();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleResize);
    };
  }, [phase]);

  // Text for each phase
  const getText = () => {
    if (phase === 0) return null;
    if (phase === 1) return { main: "ENTERING", sub: "your world", show: true };
    if (phase === 2) return { main: "ENTERING", sub: "your world", show: true };
    return null;
  };
  const textData = getText();

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 9998,
        backgroundColor: phase === 0 ? "#ffffff" : "#020617",
        overflow: "hidden",
        opacity: phase === 3 ? 0 : 1,
        transition:
          phase === 3
            ? "opacity 0.55s ease-out"
            : "background-color 0.35s ease-out",
      }}
    >
      {/* Canvas for vortex + warp */}
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      />

      {/* Center glow behind text */}
      {phase >= 1 && phase <= 2 && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: phase === 2 ? "500px" : "300px",
            height: phase === 2 ? "500px" : "300px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(245,158,11,0.15) 0%, transparent 70%)",
            transition: "all 0.6s ease-out",
            zIndex: 1,
          }}
        />
      )}

      {/* Text: ENTERING / your world */}
      {textData && textData.show && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 2,
            textAlign: "center",
            opacity: phase === 3 ? 0 : 1,
            transition: "opacity 0.4s ease-out",
          }}
        >
          <div
            style={{
              fontFamily: "'SF Mono', 'Fira Code', monospace",
              fontSize: "clamp(1.8rem, 5vw, 3rem)",
              fontWeight: 700,
              color: "#f59e0b",
              letterSpacing: "0.2em",
              textShadow:
                "0 0 30px rgba(245,158,11,0.5), 0 0 60px rgba(245,158,11,0.2)",
              animation: "portalTextPulse 1.2s ease-in-out infinite",
            }}
          >
            {textData.main}
          </div>
          <div
            style={{
              fontFamily: "'SF Mono', 'Fira Code', monospace",
              fontSize: "clamp(0.85rem, 2vw, 1.05rem)",
              fontWeight: 400,
              color: "#94a3b8",
              letterSpacing: "0.35em",
              marginTop: "0.6rem",
              textTransform: "uppercase",
              opacity: phase === 2 ? 0.5 : 0.8,
              transition: "opacity 0.4s",
            }}
          >
            {textData.sub}
          </div>

          {/* Animated dots under text */}
          <div
            style={{
              marginTop: "1.8rem",
              display: "flex",
              gap: "8px",
              justifyContent: "center",
            }}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: "#f59e0b",
                  animation: `portalDot 1.2s ease-in-out ${i * 0.2}s infinite`,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Phase 0: white flash overlay that fades */}
      {phase === 0 && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "#fff",
            zIndex: 3,
            animation: "portalFlash 0.35s ease-out forwards",
          }}
        />
      )}
    </div>
  );
};

const App = () => {
  const [appPhase, setAppPhase] = useState("intro"); // 'intro' | 'portal' | 'main'
  const [currentPage, setCurrentPage] = useState("Home");
  const [slideDirection, setSlideDirection] = useState("");
  const [isTransitioning, setIsTransitioning] = useState(false);

  const pageOrder = ["Home", "Projects", "About", "Contact"];

  const handlePageChange = (newPage) => {
    if (isTransitioning || newPage === currentPage) return;

    const currentIndex = pageOrder.indexOf(currentPage);
    const newIndex = pageOrder.indexOf(newPage);

    setIsTransitioning(true);

    if (newIndex > currentIndex) {
      setSlideDirection("slide-up");
    } else {
      setSlideDirection("slide-down");
    }

    setTimeout(() => {
      setCurrentPage(newPage);
      setSlideDirection("slide-in");
      setTimeout(() => {
        setIsTransitioning(false);
        setSlideDirection("");
      }, 600);
    }, 600);
  };

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          background-color: #020617;
          color: #f8fafc;
        }
        @keyframes slideOutUp {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(-100%); }
        }
        @keyframes slideOutDown {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(100%); }
        }
        @keyframes slideInFromBottom {
          from { opacity: 0; transform: translateY(100%); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeOut {
          from { opacity: 1; transform: scale(1); }
          to { opacity: 0; transform: scale(0); }
        }
        .slide-up { animation: slideOutUp 0.6s ease-out forwards; }
        .slide-down { animation: slideOutDown 0.6s ease-out forwards; }
        .slide-in { animation: slideInFromBottom 0.6s ease-out forwards; }
        @keyframes enterPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.25; }
        }
        @keyframes scanSweep {
          0% { top: -2px; }
          100% { top: 100vh; }
        }
        @keyframes portalFlash {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes portalTextPulse {
          0%, 100% { opacity: 1; text-shadow: 0 0 30px rgba(245,158,11,0.5), 0 0 60px rgba(245,158,11,0.2); }
          50% { opacity: 0.7; text-shadow: 0 0 50px rgba(245,158,11,0.7), 0 0 90px rgba(245,158,11,0.35); }
        }
        @keyframes portalDot {
          0%, 80%, 100% { transform: scale(0.4); opacity: 0.3; }
          40% { transform: scale(1.2); opacity: 1; }
        }
      `}</style>
      {appPhase === "intro" && (
        <HackerIntro onFinish={() => setAppPhase("portal")} />
      )}
      {appPhase === "portal" && (
        <PortalTransition onDone={() => setAppPhase("main")} />
      )}
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#020617",
          color: "#f8fafc",
          opacity: appPhase === "main" ? 1 : 0,
          transition: "opacity 0.4s ease-out",
        }}
      >
        <ThreeBackground />
        <CursorTrail />
        <div style={{ position: "relative", zIndex: 10 }}>
          <Navbar currentPage={currentPage} setCurrentPage={handlePageChange} />

          <div className={slideDirection} style={{ minHeight: "100vh" }}>
            {currentPage === "Home" && (
              <Home setCurrentPage={handlePageChange} />
            )}
            {currentPage === "Projects" && <Projects />}
            {currentPage === "About" && <About />}
            {currentPage === "Contact" && <Contact />}
          </div>

          <Footer />
        </div>
      </div>
    </>
  );
};

export default App;
